import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IClinicalCategory } from 'src/app/pages/category/clicnical-category/iclinical-category';
import { IClinicalSubCategory } from 'src/app/pages/category/clicnical-sub-category/iclinical-sub-category';
import { MaterialModule } from 'src/app/material.module';
import { OpdSubcategoryPanelComponent } from '../subcategory-panel/subcategory-panel.component';
import { OPDDetails, OPDRecord } from '../../iopddetails';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule } from '@ngx-translate/core';
import { ClinicalSubCategoryService } from '../../../../category/clicnical-sub-category/clicnical-sub-category.service';
import { ClinicalItemService } from '../../../../category/clinical-item/clinical-item.service';
import { forkJoin } from 'rxjs';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { ClinicalSubCategoryDetailsComponent } from 'src/app/pages/category/clicnical-sub-category/detail/clinical-sub-category-detail.component';
import { MatDialog } from '@angular/material/dialog';

const VIRTUAL_GENERAL_SUBCATEGORY_ID = -999;

@Component({
  selector: 'app-opd-category-panel',
  standalone: true,
  imports: [CommonModule, MaterialModule, TablerIconsModule, TranslateModule, OpdSubcategoryPanelComponent],
  templateUrl: './category-panel.component.html'
})
export class OpdCategoryPanelComponent implements OnChanges {

  @Input() category?: IClinicalCategory;
  @Input() checkedRecords: OPDRecord[] = [];
  @Input() opdId: string | null = null;
  @Input() refreshKey: number = 0;

  @Output() subcategorySelected = new EventEmitter<CommonDropDown | undefined>();
  @Output() stateChanged = new EventEmitter<void>();
  @Output() contentLoaded = new EventEmitter<{ categoryId: number; hasContent: boolean }>();

  unifiedSubcategories: IClinicalSubCategory[] = [];
  itemsBySubcategory: { [subcategoryId: number]: OPDDetails[] } = {};
  currentOpenPanel: string | null = null;
  private pendingPanelToOpen: string | null = null;
  isLoading = false;

  private actualSubcategories: IClinicalSubCategory[] = [];

  constructor(
    private _subcategoryService: ClinicalSubCategoryService,
    private _itemService: ClinicalItemService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    const categoryChanged = !!changes['category'];
    const refreshTriggered = !!changes['refreshKey'] && !categoryChanged;
    const checkedRecordsChanged = !!changes['checkedRecords'] && !categoryChanged;

    if (categoryChanged) {
      this.resetPanelState();
      this.loadCategoryContent();
    } else if (refreshTriggered) {
      this.loadCategoryContent(true);
    } else if (checkedRecordsChanged && this.unifiedSubcategories.length > 0) {
      this.cdr.markForCheck();
    }
  }

  get hasSubcategories(): boolean {
    return (this.unifiedSubcategories?.length ?? 0) > 0;
  }

  getItems(subcategoryId: number): OPDDetails[] {
    return this.itemsBySubcategory?.[subcategoryId] || [];
  }

  getPanelId(subcategory: IClinicalSubCategory): string {
    return `subcategory-${subcategory.id}`;
  }

  handlePanelOpen(panelId: string): void {
    this.currentOpenPanel = panelId;
    this.emitSelectedSubcategory(panelId);
    this.cdr.markForCheck();
  }

  handlePanelClose(panelId: string): void {
    if (this.currentOpenPanel === panelId) {
      this.currentOpenPanel = null;
      this.emitSelectedSubcategory(null);
      this.cdr.markForCheck();
    }
  }

  handleStateChanged(): void {
    this.stateChanged.emit();
  }

  trackBySubcategoryId(index: number, item: IClinicalSubCategory): number {
    return item.id;
  }

  private loadCategoryContent(keepPanelOpen: boolean = false): void {
    if (!this.category?.id) {
      this.resetCategoryState();
      return;
    }

    this.pendingPanelToOpen = keepPanelOpen ? this.currentOpenPanel : null;
    this.isLoading = true;
    this.actualSubcategories = [];
    this.resetPanelState();

    this._subcategoryService.getSubCategory({
      pageNumber: 0,
      pageSize: 100,
      isDescending: false,
      categoryId: this.category.id
    }).subscribe({
      next: (response) => {
        const subcategories = response.result?.clinicalSubCategories || [];
        this.actualSubcategories = subcategories;
        this.fetchItemsForCategory(subcategories);
      },
      error: () => {
        this.resetCategoryState();
      }
    });
  }

  private fetchItemsForCategory(subcategories: IClinicalSubCategory[]): void {
    const categoryId = this.category?.id ?? 0;

    const itemRequests = subcategories.map(sub =>
      this._itemService.getClinicalItem({
        pageNumber: 0,
        pageSize: 100,
        isDescending: false,
        categoryId,
        subcategoryID: sub.id
      })
    );

    itemRequests.push(
      this._itemService.getClinicalItem({
        pageNumber: 0,
        pageSize: 100,
        isDescending: false,
        categoryId,
        subcategoryID: null
      })
    );

    forkJoin(itemRequests).subscribe({
      next: responses => this.handleItemResponses(responses, subcategories),
      error: () => {
        this.resetCategoryState();
      }
    });
  }

  private handleItemResponses(responses: any[], subcategories: IClinicalSubCategory[]): void {
    const unifiedList: IClinicalSubCategory[] = [];
    this.itemsBySubcategory = {};

    subcategories.forEach((sub, index) => {
      const items = responses[index]?.result?.clinicalItems as OPDDetails[] || [];
      if (items.length > 0) {
        this.itemsBySubcategory[sub.id] = items;
        unifiedList.push(sub);
      }
    });

    const generalItems = responses[responses.length - 1]?.result?.clinicalItems as OPDDetails[] || [];
    if (generalItems.length > 0) {
      this.itemsBySubcategory[VIRTUAL_GENERAL_SUBCATEGORY_ID] = generalItems;
      unifiedList.unshift({
        id: VIRTUAL_GENERAL_SUBCATEGORY_ID,
        name: 'LABEL.GENERAL_ITEMS',
        description: 'General clinical items without subcategory',
        categoryId: this.category?.id ?? 0,
        seqno: 0
      } as IClinicalSubCategory);
    }

    this.unifiedSubcategories = unifiedList;
    this.isLoading = false;

    // Notify parent whether this category has content
    if (this.category?.id) {
      this.contentLoaded.emit({ categoryId: this.category.id, hasContent: unifiedList.length > 0 });
    }

    // Always open first panel on fresh load
    const candidateIds = this.unifiedSubcategories.map(sub => this.getPanelId(sub));
    const preferredPanel = this.pendingPanelToOpen;
    this.pendingPanelToOpen = null;

    const targetPanel = preferredPanel && candidateIds.includes(preferredPanel)
      ? preferredPanel
      : (candidateIds[0] ?? null);

    this.currentOpenPanel = targetPanel ?? null;
    this.emitSelectedSubcategory(targetPanel ?? null);
    this.cdr.markForCheck();
  }

  private resetCategoryState(): void {
    this.unifiedSubcategories = [];
    this.itemsBySubcategory = {};
    this.isLoading = false;
    this.currentOpenPanel = null;
    this.pendingPanelToOpen = null;
    this.emitSelectedSubcategory(null);
    this.cdr.markForCheck();
  }

  private resetPanelState(): void {
    this.currentOpenPanel = null;
  }

  private emitSelectedSubcategory(panelId: string | null): void {
    if (!panelId) {
      this.subcategorySelected.emit(undefined);
      return;
    }

    const subcategoryId = Number(panelId.replace('subcategory-', ''));
    if (Number.isNaN(subcategoryId) || subcategoryId === VIRTUAL_GENERAL_SUBCATEGORY_ID) {
      this.subcategorySelected.emit(undefined);
      return;
    }

    const matched = this.actualSubcategories.find(sub => sub.id === subcategoryId);
    if (matched) {
      this.subcategorySelected.emit({ id: matched.id, name: matched.name });
    } else {
      this.subcategorySelected.emit(undefined);
    }
  }

  AddCinicalSubcategory() {
    this.dialog.open(ClinicalSubCategoryDetailsComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      disableClose: true,
      data: { categoryId: this.category?.id }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.loadCategoryContent();
      }
    });
  }

}
